import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { track, trackMetaEventWithId } from "../lib/analytics";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { AuthShell } from "../components/auth/AuthShell";
import { CheckCircle2, MailCheck } from "lucide-react";

export default function Verify() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const magicToken = params.get("token");
  const emailParam = params.get("email") || "";

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [email, setEmail] = useState(emailParam);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [magicState, setMagicState] = useState(magicToken ? "verifying" : "idle"); // verifying|success|error|idle
  const [magicError, setMagicError] = useState("");
  const inputs = useRef([]);

  useEffect(() => {
    if (!magicToken) return;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-magic", { token: magicToken });
        login(data.user);
        setMagicState("success");
        track("email_verified", { funnel_step: 2, method: "magic_link" });
        if (data.meta_event_id) trackMetaEventWithId(data.meta_event_name || "CompleteRegistration", data.meta_event_id, { content_name: "IdeaThon 2026", content_category: "Student Innovation Competition" });
        const dest = data.user?.payment_status === "paid" ? "/team" : "/payment";
        toast.success("Email verified! Redirecting...");
        setTimeout(() => navigate(dest), 1200);
      } catch (err) {
        setMagicError(errorMessage(err));
        setMagicState("error");
      }
    })();
  }, [magicToken, navigate, login]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleOtpChange = (idx, val) => {
    const v = val.replace(/\D/g, "").slice(0, 1);
    const next = [...otp]; next[idx] = v; setOtp(next);
    if (v && idx < 5) inputs.current[idx + 1]?.focus();
    // auto-submit when last digit entered
    if (idx === 5 && v && next.every((d) => d !== "")) {
      setTimeout(() => submitOtp(), 50);
    }
  };
  const handlePaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = txt.split("").concat(Array(6).fill("")).slice(0, 6);
    setOtp(next);
    inputs.current[Math.min(txt.length, 5)]?.focus();
  };
  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) inputs.current[idx - 1]?.focus();
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) return toast.error("Enter all 6 digits");
    if (!email) return toast.error("Email missing — please register again");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { college_email: email, otp: code });
      login(data.user);
      const paid = data.user?.payment_status === "paid";
      track("email_verified", { funnel_step: 2, method: "otp" });
      if (data.meta_event_id) trackMetaEventWithId(data.meta_event_name || "CompleteRegistration", data.meta_event_id, { content_name: "IdeaThon 2026", content_category: "Student Innovation Competition" });
      toast.success(paid ? "Welcome back!" : "Verified! Redirecting to payment...");
      setTimeout(() => navigate(paid ? "/team" : "/payment"), 600);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    if (!email) return toast.error("Email missing");
    try {
      await api.post("/auth/resend", { college_email: email });
      toast.success("OTP resent");
      setCooldown(60);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (magicToken) {
    return (
      <div className="App" data-testid="verify-magic-page">
        <Navbar />
        <div className="min-h-[70vh] flex items-center justify-center p-6">
          <div className="card-surface max-w-md w-full text-center fade-up">
            {magicState === "verifying" && (<>
              <span className="spinner mb-4 inline-block" />
              <h1 className="font-display text-2xl text-white">Verifying your magic link…</h1>
            </>)}
            {magicState === "success" && (<>
              <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="font-display text-2xl text-white">Verified!</h1>
              <p className="text-gray-400 mt-2">Taking you to payment...</p>
            </>)}
            {magicState === "error" && (<>
              <h1 className="font-display text-2xl text-white">Link invalid or expired</h1>
              <p className="text-gray-400 mt-2">{magicError}</p>
              <button onClick={() => navigate("/register")} className="btn-primary mt-6" data-testid="magic-retry">Try again</button>
            </>)}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <AuthShell testid="verify-page">
        <div className="card-surface">
          <div className="label-eyebrow mb-3">Step 2 of 3</div>
          <h1 className="font-display text-3xl sm:text-4xl text-white leading-tight">Verify your college email</h1>
          <p className="text-gray-400 mt-3 leading-relaxed">
            We sent an OTP <strong className="text-white">and</strong> a magic link to <span className="text-brand break-all">{email || "your college email"}</span>. Use either method to continue.
          </p>

          {!emailParam && (
            <div className="mt-5">
              <label className="label-eyebrow block mb-2">College email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" placeholder="yourname@university.ac.in" data-testid="verify-email-input" />
            </div>
          )}

          <form onSubmit={submitOtp} className="mt-8">
            <div className="label-eyebrow mb-2">Enter 6-digit OTP</div>
            <div className="flex gap-2" onPaste={handlePaste} data-testid="otp-container">
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputs.current[i] = el)}
                  value={d}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  maxLength={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="input-field !w-full !h-14 !min-h-0 text-center font-mono text-xl !px-0"
                  data-testid={`otp-input-${i}`}
                />
              ))}
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full min-h-[48px] mt-6 flex items-center justify-center gap-2" data-testid="verify-otp-btn">
              {submitting && <span className="spinner" />} Verify OTP
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={resend} disabled={cooldown > 0} className="text-sm text-gray-400 hover:text-white disabled:text-gray-600" data-testid="resend-btn">
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
            </button>
            <button type="button" onClick={() => navigate("/register")} className="text-sm text-gray-500 hover:text-white" data-testid="back-to-register">Wrong email?</button>
          </div>

          <div className="mt-8 p-5 border border-white/10 bg-[#0F1729]">
            <div className="flex items-start gap-3">
              <MailCheck className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <div className="label-eyebrow">Magic Link Option</div>
                <div className="text-sm text-gray-400 mt-1">Open the email we sent and click <strong className="text-white">&quot;Verify My Email&quot;</strong> — it logs you in instantly, no OTP needed.</div>
              </div>
            </div>
          </div>
        </div>
    </AuthShell>
  );
}
